/**
 * EditFilterCard — Edit-mode filter card for the dashboard AI chat overlay.
 *
 * Rendered when an edit-chat message arrives with `type === 'edit_filter_card'`.
 * Pre-populates each filter's value from the `fields[].currentValue` supplied by
 * the BE (which comes from `flow_context.domainFilterValues`).
 *
 * On confirm, sends a synthesized user chat message of the form:
 *   "Use these filter values: { "<code>": "<value>" } (<label>: <value> · …)"
 * which the AI receives at `awaiting_filters` and responds to with
 * `change_filter_values intent: 'propose_values'` → transitions to
 * `awaiting_confirmation`.
 *
 * On cancel, sends "Cancel filter edit" — the AI calls
 * `change_filter_values intent: 'cancel'` → transitions back to
 * `awaiting_panels` with no state change.
 *
 * Discovery strategy — mirrors the creation-mode FiltersBlock cascading pattern:
 *   1. Non-season-dependent filters (e.g. enrollment_status) fire discovery on
 *      mount via the initial-discovery effect.
 *   2. Season-dependent filters (session, bunk) fire discovery in a cascading
 *      effect that runs whenever the user changes season selections — exactly the
 *      same parentContext: { season: [...] } pattern as FiltersBlock. This is what
 *      ensures session choices are returned as named sessions, not raw IDs.
 *
 * Discovery state lives in dashboardCreationSlice.domainFilterChoices — the same
 * Redux bucket as create-mode FiltersBlock. No new state is introduced.
 *
 * Era: dashboard-era only. No workflow-era fields (`useCaseAction`,
 * `useCaseSubject`, etc.). Distinct from `FilterConfigCard` which is
 * create-mode dead-letter.
 *
 * TDD §7.1 / Epic 3 §5.2 / plans/2026-05-12-edit-dashboard-epic-3-tdd.md
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '@/redux'
import {
  selectFilterChoices,
  selectFilterStatus,
} from '@/redux/slice/dashboardCreationSlice'
import { selectEditFlowContext } from '@/redux/slice/dashboardEditChatSlice'
import { discoverFilterValues } from '@/services/DashboardFilterDiscoveryService'
import { sendEditChatMessageThunk } from '@/services/dashboardEditChatService'
import { Button } from '@/components/ui/button'
import type { DiscoveredFilterValue } from '@/types/DashboardFilterDiscovery.types'
import type { EditFilterCardField } from '@/types/dashboardEditChat.types'

// ─── Props ────────────────────────────────────────────────────────────────────

interface EditFilterCardProps {
  fields: EditFilterCardField[]
  /**
   * True when a newer assistant turn has rendered below this card.
   * When locked, all inputs and both buttons are disabled — the card becomes
   * a read-only history record.
   */
  locked: boolean
}

// ─── FilterFieldRow ───────────────────────────────────────────────────────────

/**
 * Renders one filter input row. Dispatches on `uiHint`:
 * - `'select'` → native <select> dropdown, populated from discovered options.
 * - `'checkbox_group'` → inline checkboxes (multi-select), populated from discovered options.
 * - `'text'` → free-text <input>.
 */
interface FilterFieldRowProps {
  field: EditFilterCardField
  value: string | string[]
  onChange: (value: string | string[]) => void
  disabled: boolean
}

function FilterFieldRow({ field, value, onChange, disabled }: FilterFieldRowProps) {
  const choices =
    useSelector(selectFilterChoices(field.dashboardDomainId, field.filterCode))
  const status =
    useSelector(selectFilterStatus(field.dashboardDomainId, field.filterCode))

  const isLoading = status === 'loading'

  const currentStringValue = typeof value === 'string' ? value : ''
  const currentArrayValue = Array.isArray(value) ? value : []

  // ── select ────────────────────────────────────────────────────────────────
  if (field.uiHint === 'select') {
    const options = choices ?? []
    return (
      <div className="mb-3">
        <div className="text-[11px] font-semibold text-secondary uppercase tracking-[0.06em] mb-[4px]">
          {field.label}
        </div>
        {isLoading ? (
          <p className="text-[11px] text-muted-foreground italic">Loading options…</p>
        ) : options.length > 0 ? (
          <select
            aria-label={field.label}
            className="text-[13px] text-primary font-medium bg-card border border-border rounded-[6px] px-[8px] py-[4px] cursor-pointer focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50 disabled:cursor-default"
            value={currentStringValue}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
          >
            <option value="">— select —</option>
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.displayName}
              </option>
            ))}
          </select>
        ) : (
          // No options discovered or discovery not yet fired — show raw value as text
          <p className="text-[13px] text-primary font-medium">
            {currentStringValue || <span className="text-muted-foreground italic text-[11px]">(no current value)</span>}
          </p>
        )}
      </div>
    )
  }

  // ── checkbox_group ────────────────────────────────────────────────────────
  if (field.uiHint === 'checkbox_group') {
    const options = choices ?? []
    const handleToggle = (optValue: string, checked: boolean) => {
      const next = checked
        ? [...currentArrayValue, optValue]
        : currentArrayValue.filter(v => v !== optValue)
      onChange(next)
    }
    return (
      <div className="mb-3">
        <div className="text-[11px] font-semibold text-secondary uppercase tracking-[0.06em] mb-[4px]">
          {field.label}
        </div>
        {isLoading ? (
          <p className="text-[11px] text-muted-foreground italic">Loading options…</p>
        ) : options.length > 0 ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {options.map(opt => (
              <label
                key={opt.value}
                className={`flex items-center gap-1.5 cursor-pointer ${disabled ? 'pointer-events-none' : ''}`}
              >
                <input
                  type="checkbox"
                  className="w-[13px] h-[13px] accent-accent"
                  checked={currentArrayValue.includes(opt.value)}
                  disabled={disabled}
                  onChange={e => handleToggle(opt.value, e.target.checked)}
                />
                <span
                  className={`text-[12px] ${currentArrayValue.includes(opt.value) ? 'font-medium text-primary' : 'text-secondary'}`}
                >
                  {opt.displayName}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground italic">
            {currentArrayValue.length > 0
              ? currentArrayValue.join(', ')
              : '(no current value — options will load)'}
          </p>
        )}
      </div>
    )
  }

  // ── text (default) ────────────────────────────────────────────────────────
  return (
    <div className="mb-3">
      <div className="text-[11px] font-semibold text-secondary uppercase tracking-[0.06em] mb-[4px]">
        {field.label}
      </div>
      <input
        type="text"
        aria-label={field.label}
        className="w-full text-[13px] text-primary bg-card border border-border rounded-[6px] px-[8px] py-[4px] focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50 disabled:cursor-default"
        value={currentStringValue}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        placeholder={`Enter ${field.label.toLowerCase()}…`}
      />
    </div>
  )
}

// ─── Season-dependency helper ─────────────────────────────────────────────────

/**
 * True for filter codes whose discovery requires a season parentContext.
 * Mirrors `filterCodeNeedsSeason` in FiltersBlock — keep in sync until a
 * SDK-level `parentDependency` declaration replaces this FE-local table.
 * (Tracked: ticket #33 registry-driven runtime alternative.)
 */
function filterCodeNeedsSeason(filterCode: string): boolean {
  return filterCode === 'session' || filterCode === 'bunk'
}

// ─── EditFilterCard ───────────────────────────────────────────────────────────

export const EditFilterCard: React.FC<EditFilterCardProps> = ({ fields, locked }) => {
  const dispatch: AppDispatch = useDispatch()
  const { token } = useSelector((state: RootState) => state.user)
  const flowContext = useSelector(selectEditFlowContext)

  const credentialId = flowContext?.credentialIds?.[0] ?? null

  // Initialise local value state from the BE-supplied `currentValue` on each field.
  // This gives the user a pre-populated starting point (AC #2).
  const [values, setValues] = useState<Record<string, string | string[]>>(() =>
    Object.fromEntries(fields.map(f => [f.filterCode, f.currentValue])),
  )

  // ── Cascading discovery — mirrors FiltersBlock's season → session pattern ──
  //
  // Season values drive session (and bunk) discovery. We derive them from local
  // `values` state so they update whenever the user changes the season selection.
  // The `seasonKey` join string provides a stable dep for useEffect comparisons.
  const seasonValues = useMemo((): string[] => {
    const v = values['season']
    if (Array.isArray(v)) return v
    if (typeof v === 'string' && v !== '') return [v]
    return []
  }, [values])
  const seasonKey = seasonValues.join(',')

  // Initial discovery — fires on mount for filters that do NOT depend on season
  // (e.g. enrollment_status which is static, or any non-season-dependent
  // discovery-driven filter). Season-dependent filters (session, bunk) are
  // handled by the cascading effect below.
  //
  // Skip if locked — card is historical; no need to hit the server again.
  useEffect(() => {
    if (locked || !token || !credentialId) return
    for (const f of fields) {
      if (f.uiHint !== 'select' && f.uiHint !== 'checkbox_group') continue
      // session and bunk are season-dependent — skip here, fire in the
      // cascading effect once season values are known.
      if (filterCodeNeedsSeason(f.filterCode)) continue
      void dispatch(
        discoverFilterValues(token, f.dashboardDomainId, f.filterCode, credentialId),
      )
    }
  // Run once on mount (fields reference is stable from the parent ChatMessage).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cascading discovery — fires whenever season values change (including on
  // first non-empty season read). Mirrors FiltersBlock's cascading useEffect
  // that passes parentContext: { season: [...] } so the BE can return named
  // sessions (and bunks) scoped to the selected seasons.
  useEffect(() => {
    if (locked || !token || !credentialId || seasonValues.length === 0) return
    for (const f of fields) {
      if (f.uiHint !== 'select' && f.uiHint !== 'checkbox_group') continue
      if (!filterCodeNeedsSeason(f.filterCode)) continue
      void dispatch(
        discoverFilterValues(
          token,
          f.dashboardDomainId,
          f.filterCode,
          credentialId,
          { season: seasonValues },
        ),
      )
    }
  // Re-fire whenever season selection changes. fields is stable (parent ChatMessage).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonKey, locked, token, credentialId])

  // All discovered choices across fields — keyed by filterCode for confirm-time
  // label resolution (mirrors FiltersBlock's labelLookup at Confirm time so the
  // summary contains names, not IDs).
  const allChoicesByFilterCode = useSelector((state: RootState) => {
    const result: Record<string, DiscoveredFilterValue[] | null> = {}
    for (const f of fields) {
      const key = `${f.dashboardDomainId}.${f.filterCode}`
      result[f.filterCode] =
        state.dashboardCreation.domainFilterChoices[key] ?? null
    }
    return result
  })

  const handleValueChange = (filterCode: string, value: string | string[]) => {
    setValues(prev => ({ ...prev, [filterCode]: value }))
  }

  const handleConfirm = () => {
    if (locked || !token) return
    // Build a structured "Use these filter values" user message.
    // The AI at `awaiting_filters` has only `change_filter_values` available
    // and will call it with `intent: 'propose_values'` and the supplied values.
    const newDomainFilterValues: Record<string, string | string[]> = { ...values }

    // Resolve display names for the human-readable summary — mirrors
    // FiltersBlock's labelLookup so names (not raw IDs) appear in the summary.
    const labelLookup = (
      choices: DiscoveredFilterValue[] | null,
      vals: string[],
    ): string[] => {
      if (!choices) return vals
      const map = new Map(choices.map(c => [c.value, c.displayName]))
      return vals.map(v => map.get(v) ?? v)
    }

    const summary = fields
      .map(f => {
        const v = values[f.filterCode]
        const arr = Array.isArray(v) ? v : typeof v === 'string' && v ? [v] : []
        const labels = labelLookup(allChoicesByFilterCode[f.filterCode] ?? null, arr)
        const display = labels.length > 0 ? labels.join(', ') : '(empty)'
        return `${f.label}: ${display}`
      })
      .join(' · ')
    const message = `Use these filter values: ${JSON.stringify(newDomainFilterValues)} (${summary})`
    void dispatch(sendEditChatMessageThunk(token, message))
  }

  const handleCancel = () => {
    if (locked || !token) return
    // Post "Cancel filter edit" — the AI calls `change_filter_values intent: 'cancel'`
    // which transitions FSM awaiting_filters → awaiting_panels with no DB write.
    void dispatch(sendEditChatMessageThunk(token, 'Cancel filter edit'))
  }

  return (
    <div
      className="bg-[#f8fafc] border border-[#e2e8f0] rounded-[10px] overflow-hidden mb-[14px]"
      data-testid="edit-filter-card"
    >
      {/* Header label */}
      <div className="px-[14px] pt-[12px] pb-[8px] border-b border-[#e2e8f0]">
        <p className="text-[12px] font-semibold text-secondary uppercase tracking-[0.06em]">
          Edit filter values
        </p>
        <p className="text-[13px] text-primary mt-[2px]">
          Pick the filter values you want to apply.
        </p>
      </div>

      {/* Filter field rows */}
      <div className="px-[14px] py-[10px]">
        {fields.map(f => (
          <FilterFieldRow
            key={`${f.dashboardDomainId}.${f.filterCode}`}
            field={f}
            value={values[f.filterCode] ?? f.currentValue}
            onChange={v => handleValueChange(f.filterCode, v)}
            disabled={locked}
          />
        ))}
      </div>

      {/* Action buttons */}
      <div className="px-[14px] py-[10px] flex gap-2 border-t border-[#e2e8f0]">
        <Button
          type="button"
          className={`flex-1 font-semibold text-[13px] py-[10px] ${locked ? 'opacity-65 cursor-default pointer-events-none' : ''}`}
          onClick={handleConfirm}
          disabled={locked}
        >
          Apply filters
        </Button>
        <Button
          type="button"
          variant="outline"
          className={`font-medium text-[13px] py-[10px] ${locked ? 'opacity-60 cursor-default pointer-events-none' : ''}`}
          onClick={handleCancel}
          disabled={locked}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}
